import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  Calendar,
  CalendarDays,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Sun,
  Plane
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge
function StatusBadge({ status }) {
  const config = {
    pendiente: { label: 'Pendiente', class: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    aprobado: { label: 'Aprobado', class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    rechazado: { label: 'Rechazado', class: 'bg-red-500/20 text-red-400 border-red-500/30' },
  };
  
  const { label, class: className } = config[status] || config.pendiente;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${className}`}>
      {label}
    </span>
  );
}

// Vacation Card
function VacationCard({ vacation }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calcDays = () => {
    if (!vacation.fecha_inicio || !vacation.fecha_fin) return 0;
    const start = new Date(vacation.fecha_inicio);
    const end = new Date(vacation.fecha_fin);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Plane className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{calcDays()} días</p>
            <p className="text-xs text-zinc-500">Solicitado el {formatDate(vacation.created_at)}</p>
          </div>
        </div>
        <StatusBadge status={vacation.estado} />
      </div>
      
      <div className="flex items-center gap-4 text-sm text-zinc-400">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(vacation.fecha_inicio)}</span>
        </div>
        <span className="text-zinc-600">→</span>
        <div className="flex items-center gap-1">
          <CalendarDays className="w-4 h-4" />
          <span>{formatDate(vacation.fecha_fin)}</span>
        </div>
      </div>
      
      {vacation.motivo && (
        <p className="mt-2 text-xs text-zinc-500">{vacation.motivo}</p>
      )}
    </div>
  );
}

// Request Modal
function RequestModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    motivo: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fecha_inicio || !formData.fecha_fin) {
      toast.error('Las fechas son obligatorias');
      return;
    }

    if (new Date(formData.fecha_fin) < new Date(formData.fecha_inicio)) {
      toast.error('La fecha de fin debe ser posterior a la de inicio');
      return;
    }

    setSubmitting(true);
    await onSubmit(formData);
    setSubmitting(false);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-xl font-light text-white font-['Outfit']">Solicitar Vacaciones</h2>
          <p className="text-sm text-zinc-500 mt-1">Completa los datos de tu solicitud</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Fecha de inicio</label>
            <Input
              type="date"
              value={formData.fecha_inicio}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
              min={today}
              className="bg-white/[0.03] border-white/10 text-white"
              data-testid="vacaciones-fecha-inicio"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Fecha de fin</label>
            <Input
              type="date"
              value={formData.fecha_fin}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_fin: e.target.value }))}
              min={formData.fecha_inicio || today}
              className="bg-white/[0.03] border-white/10 text-white"
              data-testid="vacaciones-fecha-fin"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Motivo (opcional)</label>
            <textarea
              value={formData.motivo}
              onChange={(e) => setFormData(prev => ({ ...prev, motivo: e.target.value }))}
              placeholder="Vacaciones de verano, asuntos personales..."
              rows={2}
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 resize-none"
              data-testid="vacaciones-motivo"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/20 text-zinc-300"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="vacaciones-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Solicitar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function VacacionesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vacations, setVacations] = useState([]);
  const [diasDisponibles, setDiasDisponibles] = useState(22);
  const [showModal, setShowModal] = useState(false);

  const fetchVacations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/rrhh/vacaciones`, { withCredentials: true });
      setVacations(response.data.solicitudes || []);
      setDiasDisponibles(response.data.dias_disponibles || 22);
    } catch (err) {
      console.error('Error fetching vacations:', err);
      setVacations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVacations();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSubmit = async (data) => {
    try {
      await axios.post(`${API_URL}/api/rrhh/vacaciones`, data, { withCredentials: true });
      toast.success('Solicitud enviada correctamente');
      setShowModal(false);
      fetchVacations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la solicitud');
    }
  };

  const diasUsados = vacations
    .filter(v => v.estado === 'aprobado')
    .reduce((acc, v) => {
      const start = new Date(v.fecha_inicio);
      const end = new Date(v.fecha_fin);
      return acc + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }, 0);

  return (
    <ComercialLayout
      title="Vacaciones"
      subtitle="Gestiona tus días libres"
      onLogout={handleLogout}
      headerActions={
        <Button
          onClick={() => setShowModal(true)}
          className="bg-purple-500 hover:bg-purple-600 text-white"
          data-testid="solicitar-vacaciones-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Solicitud
        </Button>
      }
    >
      {/* Days Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <Sun className="w-5 h-5 text-amber-400" />
            <span className="text-sm text-zinc-400">Disponibles</span>
          </div>
          <p className="text-3xl font-light text-white font-['Outfit']">{diasDisponibles - diasUsados}</p>
          <p className="text-xs text-zinc-500 mt-1">de {diasDisponibles} días/año</p>
        </div>

        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="text-sm text-zinc-400">Usados</span>
          </div>
          <p className="text-3xl font-light text-white font-['Outfit']">{diasUsados}</p>
          <p className="text-xs text-zinc-500 mt-1">días este año</p>
        </div>

        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <span className="text-sm text-zinc-400">Pendientes</span>
          </div>
          <p className="text-3xl font-light text-white font-['Outfit']">
            {vacations.filter(v => v.estado === 'pendiente').length}
          </p>
          <p className="text-xs text-zinc-500 mt-1">solicitudes</p>
        </div>
      </div>

      {/* Vacations List */}
      <div className="space-y-4">
        <h3 className="text-lg font-light text-white font-['Outfit']">Mis Solicitudes</h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
          </div>
        ) : vacations.length === 0 ? (
          <div className="text-center py-12">
            <Plane className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No tienes solicitudes de vacaciones</p>
            <p className="text-sm text-zinc-500 mt-1">Pulsa &quot;Nueva Solicitud&quot; para crear una</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {vacations.map((vacation, index) => (
              <VacationCard key={vacation.id || index} vacation={vacation} />
            ))}
          </div>
        )}
      </div>

      {/* Info Odoo */}
      <div className="mt-8 p-4 rounded-xl bg-amber-500/[0.08] border border-amber-500/20">
        <p className="text-sm text-amber-300/80 text-center font-['Manrope']">
          Preparado para integración con Odoo RRHH. Las solicitudes se sincronizarán automáticamente.
        </p>
      </div>

      {/* Modal */}
      {showModal && (
        <RequestModal onClose={() => setShowModal(false)} onSubmit={handleSubmit} />
      )}
    </ComercialLayout>
  );
}
